import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GenericTable from '../../../common/GenericTable'
import SearchWithFilter from '../../../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../../../common/FiltersModal'
import EmptyPageResults from '../../../common/EmptyPageResults'
import LoadingSpinner from '../../../common/LoadingSpinner'
import Dropdown from '../../../common/Dropdown'
import SimpleButton from '../../../common/SimpleButton'
import { toast } from '../../../common/Toast'
import { DownloadIcon, FilterTableIcon, InformationIcon } from '../../../../icons'
import { downloadFileUTF8, getLastNYearsRangeISO, sortModelsAlphabetically } from '../../../../utils/functions'
import { useWindowSize } from '../../../../libs/hooks'
import { useDeepCompareEffect } from '../../../../hooks/useDeepCompareEffect'
import { loadRegions } from '../../../../actions/shared'
import { loadFirmwareModels } from '../../../../actions/firmware'
import {
  clearReportsMessages,
  downloadFirmwareDownloadData,
  loadFirmwareDownloadStatisticsData,
  loadFirmwareDownloadCountries,
} from '../../../../actions/reports'
import SoftwareDownloadsChart from './SoftwareDownloadsChart'
import '../../internal-reports.scss'

const orderingFields = [
  {
    field: 'model',
    order: 'asc',
    translationKey: 'model_asc',
  },
  {
    field: 'model',
    order: 'desc',
    translationKey: 'model_desc',
  },
  {
    field: 'monthYear',
    order: 'asc',
    translationKey: 'date_asc',
  },
  {
    field: 'monthYear',
    order: 'desc',
    translationKey: 'date_desc',
  },
  {
    field: 'accountType',
    order: 'asc',
    translationKey: 'account_type_asc',
  },
  {
    field: 'accountType',
    order: 'desc',
    translationKey: 'account_type_desc',
  },
  {
    field: 'downloadTotal',
    order: 'asc',
    translationKey: 'total_downloads_asc',
  },
  {
    field: 'downloadTotal',
    order: 'desc',
    translationKey: 'total_downloads_desc',
  },
]

const { startISO: defaultStartISO, endISO: defaultEndISO, timeZone } = getLastNYearsRangeISO(5)

const SoftwareDownloadsReports = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [firmwareDownloadTableData, setFirmwareDownloadTableData] = useState([])
  const [orderBy, setOrderBy] = useState(null)
  const [selectedAccountType, setSelectedAccountType] = useState([])
  const [selectedFilterCountry, setSelectedFilterCountry] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState([])
  const [selectedFilterRegion, setSelectedFilterRegion] = useState([])
  const [startDateFilter, setStartDateFilter] = useState(defaultStartISO)
  const [endDateFilter, setEndDateFilter] = useState(defaultEndISO)
  const [filtersInitialized, setFiltersInitialized] = useState(false)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showChartFiltersModal, setShowChartFiltersModal] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [chartFrequency, setChartFrequency] = useState('year')
  const [chartType, setChartType] = useState('bar')
  const [expandedData, setExpandedData] = useState(0)
  const [statsVisibleCount, setStatsVisibleCount] = useState(10)
  const chartData = props.firmwareDownloadStatistics
  const accountTypeFilters = ['Coltene', 'Dealer']
  const chartTypeKeys = ['line', 'bar', 'stackedBar', 'pie']
  const chartFrequencyKeys = ['month', 'quarter', 'year']

  const selectedChartData = useMemo(() => {
    const src = chartData
    if (Array.isArray(src)) return src
    if (!src) return []
    return src[chartFrequency] || []
  }, [chartData, chartFrequency])

  const hasChart = Array.isArray(selectedChartData) && selectedChartData.length > 0
  const hasTable = firmwareDownloadTableData.length > 0

  const chartFilterOptions = [
    {
      name: t('reports.frequency'),
      type: FilterType.singleSelect,
      dataset: chartFrequencyKeys,
      input: chartFrequency,
      output: (selected) => setChartFrequency(selected),
      required: true,
    },
    {
      name: t('reports.chart_type'),
      type: FilterType.singleSelect,
      dataset: chartTypeKeys,
      input: chartType,
      output: (selected) => setChartType(selected),
      required: true,
    },
  ]

  useEffect(() => {
    if (!filtersInitialized && selectedFilterModel.length === 0 && props.g4ModelNames?.length) {
      setSelectedFilterModel(props.g4ModelNames)
      setFiltersInitialized(true)
    }
  }, [selectedFilterModel.length, props.g4ModelNames])

  useEffect(() => {
    if (props.firmwareModels.length === 0) {
      props.actions.loadFirmwareModels()
    }

    if (props.firmwareDownloadCountries.length === 0) {
      props.actions.loadFirmwareDownloadCountries()
    }

    if (props.reportsRegions.length === 0) {
      props.actions.loadRegions()
    }
  }, [props.actions])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.successMessage])

  useDeepCompareEffect(() => {
    if (!filtersInitialized) {
      return
    }

    handleLoadChartData()
  }, [filtersInitialized, selectedAccountType, startDateFilter, endDateFilter, selectedFilterCountry, selectedFilterModel, selectedFilterRegion, orderBy])

  useEffect(() => {
    const tableData = props.firmwareDownloadStatistics.map((item, index) => ({
      index,
      accountType: item.accountType,
      date: `${item.year}-${item.month.toString().padStart(2, '0')}`,
      country: item.country,
      region: item.region,
      model: item.model,
      totalDownloads: item.totalDownload,
    }))

    setFirmwareDownloadTableData(tableData)
    setStatsVisibleCount(10)
  }, [props.firmwareDownloadStatistics])

  const getUniqueRegions = (regions) => {
    if (!regions || regions.length === 0) {
      return []
    }

    const names = regions.map((region) => region.name)
    const uniqueNames = [...new Set(names)]
    return uniqueNames.sort((a, b) => a.localeCompare(b))
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (selectedAccountType.length === 1) {
      queryParams = { ...queryParams, accountType: selectedAccountType[0] }
    }

    if (selectedFilterModel.length > 0) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterRegion.length > 0) {
      queryParams = { ...queryParams, region: selectedFilterRegion }
    }

    if (selectedFilterCountry.length > 0) {
      queryParams = { ...queryParams, country: selectedFilterCountry }
    }

    if (startDateFilter) {
      queryParams = { ...queryParams, startDate: startDateFilter }
    }

    if (endDateFilter) {
      queryParams = { ...queryParams, endDate: endDateFilter }
    }

    if (startDateFilter && endDateFilter) {
      queryParams = { ...queryParams, timezone: timeZone }
    }

    return queryParams
  }

  const handleLoadChartData = () => {
    let queryParams = {}

    if (selectedAccountType.length === 1) {
      queryParams = { ...queryParams, accountType: selectedAccountType[0] }
    }

    if (selectedFilterModel.length > 0) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterRegion.length > 0) {
      queryParams = { ...queryParams, region: selectedFilterRegion }
    }

    if (selectedFilterCountry.length > 0) {
      queryParams = { ...queryParams, country: selectedFilterCountry }
    }

    if (startDateFilter) {
      queryParams = { ...queryParams, startDate: startDateFilter }
    }

    if (endDateFilter) {
      queryParams = { ...queryParams, endDate: endDateFilter }
    }

    if (startDateFilter && endDateFilter) {
      queryParams = { ...queryParams, timezone: timeZone }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    props.actions.loadFirmwareDownloadStatisticsData(queryParams).then(() => setIsFirstLoad(false))
  }

  const handleResetFilters = () => {
    setSelectedAccountType([])
    setSelectedFilterModel([])
    setSelectedFilterRegion([])
    setSelectedFilterCountry([])
    setStartDateFilter(null)
    setEndDateFilter(null)
  }

  const handleResetChartFilters = () => {
    setChartFrequency('')
    setChartType('')
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleDownload = () => {
    let queryParams = createQueryParams()

    props.actions.downloadFirmwareDownloadData(queryParams).then((res) => {
      const date = new Date().toISOString().split('T')
      const dateNow = date[0].split('-').reverse().join('_')
      const timeNow = date[1].split('.')[0].split(':').join('_')

      downloadFileUTF8({
        data: res.value,
        fileName: `firmware-downloads_${dateNow}_${timeNow}.csv`,
        fileType: 'text/csv',
      })
    })
  }

  const renderChartSection = () => {
    if (hasChart) {
      return (
        <div className={`chart-section ${chartType === 'pie' ? 'pie-chart' : ''}`}>
          <div className="chart-filters-row">
            <div className="chart-title">{t('reports.software_downloads_chart')}</div>
            <SimpleButton className="filter-button" onClick={() => setShowChartFiltersModal(true)}>
              <div className="icon-wrapper">
                <FilterTableIcon />
              </div>
              {t('reports.chart_styles')}
            </SimpleButton>
          </div>
          <SoftwareDownloadsChart
            g4ModelNames={props.g4ModelNames}
            data={selectedChartData}
            chartType={chartType}
            chartFrequency={chartFrequency}
            hasSelectedModelFilter={selectedFilterModel.length > 0}
            selectedModelNames={selectedFilterModel}
            key={`chart-${chartType}-${chartFrequency}-${selectedChartData.length}`}
          />
          {showChartFiltersModal && (
            <div className="chart-filters-modal-container">
              <FiltersModal
                filters={chartFilterOptions}
                name="styles"
                closeFilters={() => setShowChartFiltersModal(false)}
                modalClassName="chart-filters-modal"
                resetFilters={handleResetChartFilters}
                requireAllRequiredFilters
              />
            </div>
          )}
        </div>
      )
    }
  }

  const renderFirmwareTable = () => {
    if (!props.isLoading && !isFirstLoad && firmwareDownloadTableData.length === 0) {
      return <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
    }

    if (!isMobile) {
      return (
        <GenericTable
          data={firmwareDownloadTableData.slice(0, statsVisibleCount)}
          headers={[
            {
              title: t('model'),
              orderKey: 'model',
            },
            {
              title: t('date'),
              orderKey: 'monthYear',
            },
            {
              title: t('account_context.account_type'),
              orderKey: 'accountType',
            },
            {
              title: t('total_downloads'),
              orderKey: 'downloadTotal',
            },
          ]}
          keys={['model', 'date', 'accountType', 'totalDownloads']}
          activeSort={orderBy}
          onSort={(orderingField) => handleSort(orderingField)}
          isLoading={props.isLoading}
        />
      )
    }
    return (
      <>
        {firmwareDownloadTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => {
                const sortOption = orderingFields.find((f) => f.translationKey === orderBy.key)
                handleSort(sortOption)
              }}
            />
          </div>
        )}

        {firmwareDownloadTableData.slice(0, statsVisibleCount).map((item, index) => (
          <div key={index} className="card" onClick={() => setExpandedData(index)}>
            <div className={'card-item' + (expandedData !== index ? ' align-center' : '')}>
              <div className="card-item-title">{t('model')}</div>
              <div className={'card-item-body' + (expandedData !== index ? ' align-center' : '')}>
                <div>{item.model}</div>
              </div>
            </div>
            {expandedData === index && (
              <>
                <div className="card-item">
                  <div className="card-item-title">{t('date')}</div>
                  <div className="card-item-body">{item.date}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('account_context.account_type')}</div>
                  <div className="card-item-body">{item.accountType}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('total_downloads')}</div>
                  <div className="card-item-body">{item.totalDownloads}</div>
                </div>
              </>
            )}
          </div>
        ))}
      </>
    )
  }

  const renderTableSection = () => (
    <div className="table-section">
      <div className="actions-wrapper">
        <div className="table-actions-row">
          <SimpleButton className="action-button" onClick={() => handleDownload()} disabled={props.isLoading || !hasTable}>
            <DownloadIcon />
            {t('download')}
          </SimpleButton>
        </div>
      </div>
      {renderFirmwareTable()}
      {statsVisibleCount < firmwareDownloadTableData.length && (
        <div className="buttons-wrapper">
          <SimpleButton className="load-more-button" onClick={() => setStatsVisibleCount(statsVisibleCount + 10)}>
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
    </div>
  )

  return (
    <div className="internal-reports-section firmware-downloads">
      <div className="actions-wrapper page-section">
        <SearchWithFilter
          showFiltersModal={() => setShowFiltersModal(true)}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={
            (startDateFilter && endDateFilter) ||
            selectedFilterModel.length > 0 ||
            selectedFilterRegion.length > 0 ||
            selectedFilterCountry.length > 0 ||
            selectedAccountType.length > 0
          }
          counterBadge={
            selectedFilterModel.length +
            selectedFilterRegion.length +
            selectedFilterCountry.length +
            selectedAccountType.length +
            (startDateFilter && endDateFilter ? 1 : 0)
          }
        />
      </div>
      <div className="disclaimer-box-text">
        <div className="icon-wrapper">
          <InformationIcon />
        </div>
        <div className="disclaimer-wrapper">
          <div className="title">{t('reports.software_download_chart_disclaimer')}</div>
          <div className="title">{t('reports.date_disclaimer')}</div>
        </div>
      </div>
      {!hasChart && !hasTable && !isFirstLoad ? (
        <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
      ) : (
        <>
          {renderChartSection()}
          {renderTableSection()}
        </>
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('date'),
              type: FilterType.dateRange,
              dataset: null,
              input: { startDate: startDateFilter, endDate: endDateFilter },
              output: (startDate, endDate) => {
                setStartDateFilter(startDate)
                setEndDateFilter(endDate)
              },
              quickLinks: [
                { label: t('1_month'), months: 1 },
                { label: t('3_months'), months: 3 },
                { label: t('6_months'), months: 6 },
                { label: t('12_months'), months: 12 },
              ],
            },
            {
              name: t('model'),
              type: FilterType.multiSelect,
              dataset: props.firmwareModels,
              input: selectedFilterModel,
              output: (model) => setSelectedFilterModel(model),
            },
            {
              name: t('account_context.account_type'),
              type: FilterType.multiSelect,
              dataset: accountTypeFilters,
              input: selectedAccountType,
              output: (status) => setSelectedAccountType(status),
            },
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: props.firmwareDownloadCountries.map((c) => c.name),
              input: selectedFilterCountry,
              output: (country) => setSelectedFilterCountry(country),
            },
            {
              name: t('region'),
              type: FilterType.multiSelect,
              dataset: getUniqueRegions(props.reportsRegions),
              input: selectedFilterRegion,
              output: (region) => setSelectedFilterRegion(region),
            },
          ]}
          shouldFilter={(shouldApply) => {
            setFiltersInitialized(shouldApply)
          }}
          resetFilters={handleResetFilters}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {props.isLoading && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function stateToProps({ reports, shared, firmware }) {
  const modelData = firmware?.firmwareModels || []
  const firmwareModels = [...new Set(modelData.map((m) => m.model || '').filter(Boolean))]
  const g4ModelNames = [
    ...new Set(
      modelData
        .filter((m) => m.is_g4 || m.is_g4_plus)
        .map((m) => m.model || '')
        .filter(Boolean),
    ),
  ]

  return {
    firmwareDownloadStatistics: reports?.firmwareDownloadStatisticsData || [],
    firmwareDownloadTable: reports?.firmwareDownloadTableData || [],
    firmwareDownloadCountries: reports?.firmwareDownloadCountries || [],
    reportsRegions: shared?.regions || [],
    firmwareModels: sortModelsAlphabetically(firmwareModels),
    g4ModelNames,
    isLoading: reports?.isLoading,
    canLoadMore: reports?.canLoadMoreTableData,
    errorMessage: reports?.errorMessage,
    successMessage: reports?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadFirmwareDownloadStatisticsData,
        loadFirmwareDownloadCountries,
        downloadFirmwareDownloadData,
        clearReportsMessages,
        loadFirmwareModels,
        loadRegions,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(SoftwareDownloadsReports)

